import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { TemplatePtAPI } from "@repo/api";
import { Template } from "@/types/template_trace";
import { useCallback, useEffect, useRef, useState } from "react";
import {
    Alert,
    Button,
    Checkbox,
    Chip,
    cn,
    Input,
    Link,
    Modal,
    Pagination,
    Separator,
    Spinner,
    Surface,
    Tabs,
    TextArea,
    Tooltip,
    toast,
    useOverlayState,
} from "@heroui/react";
import { User as UserType } from "@/types/user";
import { UserSearch } from "@repo/ui/components/UserSearch";
import { UserAPI } from "@repo/api/user";
import { useAuth } from "@repo/context/auth/auth";
import { CheckCircle2, HelpCircle, Package, Plus, Search, ShieldAlert, X } from "lucide-react";
import { getRouteApi } from "@tanstack/react-router";
import { asSearchString } from "@repo/utils/search-params";
import ComponentDynamicUser from "@repo/ui/pages/template_service/components/ComponentUser";
import { BlacklistServiceAPI } from "@repo/api/blacklist";
import type { BatchCheckResultItem } from "@repo/api/blacklist.type";

export type SendSearch = {
    templateId?: string;
    templateTitle?: string;
    templatePreviewUrl?: string;
};

export const Route = createFileRoute("/template_protections/send")({
    validateSearch: (search: Record<string, unknown>): SendSearch => ({
        templateId: asSearchString(search.templateId),
        templateTitle: asSearchString(search.templateTitle),
        templatePreviewUrl: asSearchString(search.templatePreviewUrl),
    }),
    component: TemplateSendPage,
});

const sendRouteApi = getRouteApi("/template_protections/send");

const MODAL_PAGE_SIZE = 10;

function buildPageItems(current: number, total: number): Array<number | "ellipsis"> {
    if (total <= 7) {
        return Array.from({ length: total }, (_, i) => i + 1);
    }

    const pages: Array<number | "ellipsis"> = [1];
    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);

    if (start > 2) pages.push("ellipsis");
    for (let p = start; p <= end; p++) pages.push(p);
    if (end < total - 1) pages.push("ellipsis");
    pages.push(total);
    return pages;
}

export default function TemplateSendPage() {
    const navigate = useNavigate();
    const { userInfo } = useAuth();
    const { templateId: urlTemplateId, templateTitle: urlTemplateTitle, templatePreviewUrl: urlTemplatePreviewUrl } =
        sendRouteApi.useSearch();

    // 模板选择相关状态
    const [selectedTemplates, setSelectedTemplates] = useState<Template[]>([]);
    const initializedFromUrl = useRef(false);

    // Modal 相关状态
    const { isOpen: isTemplateModalOpen, open: openTemplateModal, close: closeTemplateModal, setOpen: setTemplateModalOpen } =
        useOverlayState();
    const [modalTemplates, setModalTemplates] = useState<Template[]>([]);
    const [modalSelectedIds, setModalSelectedIds] = useState<Set<string>>(new Set());
    const [modalSelectedCache, setModalSelectedCache] = useState<Record<string, Template>>({});
    const [modalSearchValue, setModalSearchValue] = useState("");
    const [modalLoading, setModalLoading] = useState(false);
    const [modalPage, setModalPage] = useState(1);
    const [modalTotalPages, setModalTotalPages] = useState(1);
    const [modalTotal, setModalTotal] = useState(0);

    // 收货人选择相关状态
    const [selectedUser, setSelectedUser] = useState<UserType.UserSearchItem[]>([]);
    const [qqList, setQqList] = useState<string[]>([]);
    const [uidList, setUidList] = useState<string[]>([]);
    const [searchValue, setSearchValue] = useState<string>("");
    const [isConfirmed, setIsConfirmed] = useState(false);
    const [qqBatchLoading, setQqBatchLoading] = useState(false);
    const [uidBatchLoading, setUidBatchLoading] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);

    // 用户使用统计
    const [userStatistics, setUserStatistics] = useState<Record<string, { send_count: number; receive_count: number }>>({});

    // 黑名单命中结果：uid → 命中详情
    const [blacklistHits, setBlacklistHits] = useState<Record<string, BatchCheckResultItem>>({});
    const blacklistCheckTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [blacklistMatching, setBlacklistMatching] = useState(false);

    // 从 URL 参数初始化默认选中模板
    useEffect(() => {
        if (initializedFromUrl.current) return;
        if (!urlTemplateId || !urlTemplateTitle) return;

        // 创建一个临时的模板对象用于显示（只需要 id, title, preview_url 用于界面展示）
        const template = {
            id: urlTemplateId,
            title: urlTemplateTitle,
            preview_url: urlTemplatePreviewUrl || "",
        } as unknown as Template;

        setSelectedTemplates([template]);

        initializedFromUrl.current = true;
    }, [urlTemplateId, urlTemplateTitle, urlTemplatePreviewUrl]);

    // 当选中用户变化时，查询使用统计
    useEffect(() => {
        if (selectedUser.length === 0) {
            setUserStatistics({});
            return;
        }

        // 找出还没有统计数据的用户
        const newUids = selectedUser
            .filter(user => !(user.uid in userStatistics))
            .map(user => user.uid);

        if (newUids.length === 0) return;

        TemplatePtAPI.getUseStatistics(newUids)
            .then(({ data }) => {
                const newStats: Record<string, { send_count: number; receive_count: number }> = {};
                data.forEach(item => {
                    newStats[item.uid] = {
                        send_count: item.send_count,
                        receive_count: item.receive_count,
                    };
                });
                setUserStatistics(prev => ({ ...prev, ...newStats }));
            })
            .catch((error) => {
                console.error("查询使用统计失败:", error);
            });
    }, [selectedUser]);

    // 监听 selectedUser，防抖后批量检查黑名单
    useEffect(() => {
        if (blacklistCheckTimer.current) {
            clearTimeout(blacklistCheckTimer.current);
        }

        if (selectedUser.length === 0) {
            setBlacklistHits({});
            return;
        }

        setBlacklistMatching(true);
        blacklistCheckTimer.current = setTimeout(async () => {
            const uids = selectedUser.map(u => u.uid);
            try {
                const { data } = await BlacklistServiceAPI.checkUidBatch(uids);
                const hits: Record<string, BatchCheckResultItem> = {};
                for (const item of data.results ?? []) {
                    if (item.hit) {
                        hits[item.target_uid] = item;
                    }
                }
                setBlacklistHits(hits);
            } catch (e) {
                console.error("黑名单检查失败:", e);
            } finally {
                setBlacklistMatching(false);
            }
        }, 1000);

        return () => {
            if (blacklistCheckTimer.current) {
                clearTimeout(blacklistCheckTimer.current);
            }
        };
    }, [selectedUser]);

    // 加载 Modal 中的模板列表
    const loadModalTemplates = useCallback(async (searchKeyword = "", page = 1) => {
        setModalLoading(true);
        try {
            const { data } = await TemplatePtAPI.getTemplateList(page, MODAL_PAGE_SIZE, searchKeyword);
            setModalTemplates(data.content ?? []);
            setModalPage(data.page_num || page);
            setModalTotalPages(Math.max(1, data.total_pages || 1));
            setModalTotal(data.total || 0);
        } catch (error) {
            toast.danger("加载模板失败");
            console.error("加载模板失败:", error);
        } finally {
            setModalLoading(false);
        }
    }, []);

    // 打开 Modal
    const handleOpenModal = useCallback(() => {
        setModalSearchValue("");
        setModalSelectedIds(new Set(selectedTemplates.map(t => t.id)));
        setModalSelectedCache(Object.fromEntries(selectedTemplates.map(t => [t.id, t])));
        openTemplateModal();
        loadModalTemplates("", 1);
    }, [selectedTemplates, loadModalTemplates, openTemplateModal]);

    // 处理 Modal 中的搜索
    const handleModalSearch = useCallback(() => {
        loadModalTemplates(modalSearchValue, 1);
    }, [modalSearchValue, loadModalTemplates]);

    const goToModalPage = useCallback((page: number) => {
        if (page === modalPage || modalLoading) return;
        loadModalTemplates(modalSearchValue, page);
    }, [modalPage, modalLoading, modalSearchValue, loadModalTemplates]);

    // 处理 Modal 中的模板选择（勾选状态跨页保留）
    const toggleModalTemplateSelection = useCallback((templateId: string) => {
        const template = modalTemplates.find(t => t.id === templateId);
        setModalSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(templateId)) {
                next.delete(templateId);
            } else {
                next.add(templateId);
            }
            return next;
        });
        setModalSelectedCache(prev => {
            if (prev[templateId]) {
                const next = { ...prev };
                delete next[templateId];
                return next;
            }
            if (!template) return prev;
            return { ...prev, [templateId]: template };
        });
    }, [modalTemplates]);

    // 用弹窗里的当前勾选覆盖发货列表（含其它页勾选 / 取消勾选）
    const handleConfirmTemplates = useCallback(() => {
        const nextTemplates = Array.from(modalSelectedIds)
            .map((id) => modalSelectedCache[id] ?? modalTemplates.find(t => t.id === id))
            .filter((t): t is Template => !!t);

        if (nextTemplates.length === 0) {
            toast.danger("请至少选择一个模板");
            return;
        }

        setSelectedTemplates(nextTemplates);
        closeTemplateModal();
    }, [modalSelectedIds, modalSelectedCache, modalTemplates, closeTemplateModal]);

    // 移除选中的模板
    const removeSelectedTemplate = useCallback((templateId: string) => {
        setSelectedTemplates(prev => prev.filter(t => t.id !== templateId));
    }, []);

    // 添加选中用户
    const addSelectedUser = (user: UserType.UserSearchItem) => {
        if (selectedUser.some((u) => u.uid === user.uid)) {
            return;
        }
        setSelectedUser((prev) => [...prev, user]);
    };

    // 移除选中用户
    const removeSelectedUser = (user: UserType.UserSearchItem) => {
        if (!selectedUser.some((u) => u.uid === user.uid)) {
            return;
        }
        setSelectedUser((prev) => prev.filter((u) => u !== user));
        // 清理对应的统计数据
        setUserStatistics((prev) => {
            const newStats = { ...prev };
            delete newStats[user.uid];
            return newStats;
        });
    };

    // QQ批量查询功能
    const handleBatchQueryQQ = async () => {
        if (qqList.length === 0 || qqList.every(qq => qq.trim() === "")) {
            toast.danger("请输入至少一个QQ号");
            return;
        }

        const validQqList = qqList.filter(qq => qq.trim() !== "");

        if (validQqList.length === 0) {
            toast.danger("请输入有效的QQ号");
            return;
        }

        if (validQqList.length > 100) {
            toast.danger("一次最多只能识别100个QQ号，请分批进行");
            return;
        }

        setQqBatchLoading(true);

        try {
            const { data } = await UserAPI.batchQueryByQQ(validQqList);

            const newUsers = data.hit.filter(user =>
                !selectedUser.some(selected => selected.uid === user.uid)
            );
            setSelectedUser(prev => [...prev, ...newUsers]);
            setQqList(data.missed);

            const hitCount = data.hit.length;
            const missedCount = data.missed.length;

            if (hitCount > 0 && missedCount > 0) {
                toast.success(`已添加 ${hitCount} 个用户，成功添加的用户已从搜索框中剔除`);
            } else if (hitCount > 0) {
                toast.success(`已添加 ${hitCount} 个用户`);
            } else {
                toast.warning(`没有匹配到任何用户`);
            }
        } catch (error: any) {
            toast.danger(error.msg || "查询失败，请稍后重试");
            console.error("QQ批量查询失败:", error);
        } finally {
            setQqBatchLoading(false);
        }
    };

    // UID批量查询功能
    const handleBatchQueryUID = async () => {
        if (uidList.length === 0 || uidList.every(uid => uid.trim() === "")) {
            toast.danger("请输入至少一个UID");
            return;
        }

        const validUidList = uidList.filter(uid => uid.trim() !== "");

        if (validUidList.length === 0) {
            toast.danger("请输入有效的UID");
            return;
        }

        if (validUidList.length > 100) {
            toast.danger("一次最多只能识别100个UID，请分批进行");
            return;
        }

        setUidBatchLoading(true);

        try {
            const { data } = await UserAPI.batchQueryByUIDs(validUidList);

            const newUsers = data.hit.filter(user =>
                !selectedUser.some(selected => selected.uid === user.uid)
            );
            setSelectedUser(prev => [...prev, ...newUsers]);
            setUidList(data.missed);

            const hitCount = data.hit.length;
            const missedCount = data.missed.length;

            if (hitCount > 0 && missedCount > 0) {
                toast.success(`已添加 ${hitCount} 个用户，成功添加的用户已从搜索框中剔除`);
            } else if (hitCount > 0) {
                toast.success(`已添加 ${hitCount} 个用户`);
            } else {
                toast.danger(`没有匹配到任何用户`);
            }
        } catch (error: any) {
            toast.danger(error.msg || "查询失败，请稍后重试");
            console.error("UID批量查询失败:", error);
        } finally {
            setUidBatchLoading(false);
        }
    };

    // 执行发货
    const handleSubmit = async () => {
        if (selectedTemplates.length === 0) {
            toast.danger("请先选择要发送的模板");
            return;
        }

        if (selectedUser.length === 0) {
            toast.danger("请至少选择一个收货人哦～");
            return;
        }

        if (selectedUser.some((user) => user.uid === userInfo?.uid)) {
            toast.danger("您不能发货给自己哦～");
            return;
        }

        setSubmitLoading(true);

        try {
            // 一次性发送所有模板
            await TemplatePtAPI.sendTemplate(
                selectedTemplates.map(template => template.id),
                selectedUser.map((user) => user.uid),
            );

            toast.success(`成功发送 ${selectedTemplates.length} 个模板给 ${selectedUser.length} 位用户`);
            // 重置状态
            setSelectedUser([]);
            setSelectedTemplates([]);
            setIsConfirmed(false);
        } catch (error: any) {
            toast.danger(error.msg || "发货失败");
            console.error("发货失败:", error);
        } finally {
            setSubmitLoading(false);
        }
    };

    const uidInputCount = uidList.filter(uid => uid.trim() !== "").length;
    const qqInputCount = qqList.filter(qq => qq.trim() !== "").length;

    return (
        <section className="md:max-w-7xl mx-auto min-h-screen bg-background">
            <div className="px-4 sm:px-6 lg:px-11 py-6 sm:py-9">
                <Surface className="rounded-xl shadow-sm p-6 md:p-8 max-w-5xl mx-auto">
                    {/* 页面标题 */}
                    <div className="text-center mb-8">
                        <h1 className="text-xl font-bold text-foreground">发送模板</h1>
                        <p className="text-sm text-muted mt-1">选择模板和收货人，快速完成发货</p>
                    </div>

                    {/* STEP 1: 模板选择器 */}
                    <section className="mb-8">
                        <div className="flex items-center justify-between gap-3 mb-4">
                            <div className="flex items-center gap-2">
                                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-accent text-accent-foreground text-xs font-medium">1</span>
                                <h2 className="text-base font-medium leading-none text-foreground">
                                    选择需要发送的模板
                                </h2>
                            </div>
                            <Button size="sm" variant="primary" onPress={handleOpenModal}>
                                <Plus size={20} />
                                添加
                            </Button>
                        </div>

                        {selectedTemplates.length === 0 ? (
                            <div className="flex items-center justify-center py-12 bg-surface-secondary rounded-lg border-2 border-dashed border-border">
                                <p className="text-muted text-sm">请点击右上角添加需要发送的模板</p>
                            </div>
                        ) : (
                            <div className="mt-2">
                                <div className="flex items-center justify-between mb-3">
                                    <span className="text-sm font-medium text-foreground">已选择的模板</span>
                                    <span className="text-xs text-muted">{selectedTemplates.length} 个模板</span>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {selectedTemplates.map((template) => (
                                        <div key={template.id} className="group relative bg-surface-secondary hover:bg-surface-tertiary rounded-lg p-3 transition-colors">
                                            <Button
                                                size="sm"
                                                isIconOnly
                                                aria-label={`移除模板 ${template.title}`}
                                                variant="secondary"
                                                className="absolute -top-2 -right-2 rounded-full w-6 h-6 min-w-0 opacity-0 group-hover:opacity-100 transition-opacity z-10"
                                                onPress={() => removeSelectedTemplate(template.id)}
                                            >
                                                <X size={14} />
                                            </Button>
                                            <div className="flex gap-3 items-center">
                                                <div className="w-16 h-16 shrink-0">
                                                    <img
                                                        alt={template.title}
                                                        className="w-full h-full object-cover rounded-lg"
                                                        src={template.preview_url}
                                                    />
                                                </div>
                                                <div className="flex flex-col flex-1 min-w-0">
                                                    <span className="text-sm font-medium text-foreground truncate">{template.title}</span>
                                                    <span className="text-xs text-muted truncate">{template.description}</span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </section>

                    <Separator className="my-8" orientation="horizontal" />

                    {/* STEP 2: 选择收货人 */}
                    <section className="mb-8">
                        <div className="flex items-center gap-3 mb-4">
                            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-accent text-accent-foreground text-xs font-medium">2</span>
                            <h2 className="text-base font-medium text-foreground">选择收货人</h2>
                        </div>
                        <Tabs>
                            <Tabs.ListContainer>
                                <Tabs.List aria-label="收货人选择方式">
                                    <Tabs.Tab id="search_by_uid" className="text-sm">
                                        UID搜索
                                        <Tabs.Indicator />
                                    </Tabs.Tab>
                                    <Tabs.Tab id="search_by_uid_batch" className="text-sm">
                                        <Tabs.Separator />
                                        UID批量
                                        <Tabs.Indicator />
                                    </Tabs.Tab>
                                    <Tabs.Tab id="search_by_qq" className="text-sm">
                                        <Tabs.Separator />
                                        QQ批量
                                        <Tabs.Indicator />
                                    </Tabs.Tab>
                                </Tabs.List>
                            </Tabs.ListContainer>
                            <Tabs.Panel id="search_by_uid">
                                <div className="py-4">
                                    <UserSearch
                                        searchFn={UserAPI.searchUser}
                                        placeholder="输入收货人ID或昵称来搜索"
                                        value={searchValue}
                                        onInputChange={setSearchValue}
                                        onSelect={(user) => {
                                            addSelectedUser(user);
                                        }}
                                    />
                                </div>
                            </Tabs.Panel>
                            <Tabs.Panel id="search_by_uid_batch">
                                <div className="flex flex-col gap-3 py-4">
                                    <TextArea
                                        fullWidth
                                        rows={6}
                                        placeholder="输入收货人UID，一行一个"
                                        value={uidList.join("\n")}
                                        onChange={(e) => setUidList(e.target.value.split("\n"))}
                                        variant="secondary"
                                    />
                                    <div className="flex justify-between items-center text-xs">
                                        <span className={cn(uidInputCount > 100 ? "text-danger" : "text-muted")}>
                                            已输入 {uidInputCount} 个UID
                                        </span>
                                        <span className="text-muted">最多100个</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <Button
                                            variant="primary"
                                            onPress={handleBatchQueryUID}
                                            isPending={uidBatchLoading}
                                            isDisabled={uidInputCount === 0 || uidInputCount > 100}
                                        >
                                            {({ isPending }) => (
                                                <>
                                                    {isPending && <Spinner size="sm" color="current" />}
                                                    {isPending ? "查询中..." : "批量添加"}
                                                </>
                                            )}
                                        </Button>
                                        <Tooltip>
                                            <Tooltip.Trigger>
                                                <div className="flex items-center gap-1 text-muted cursor-help">
                                                    <HelpCircle size={14} />
                                                    <span className="text-xs">帮助</span>
                                                </div>
                                            </Tooltip.Trigger>
                                            <Tooltip.Content>
                                                如果输入的UID不存在或无效，则无法识别。请确认UID是否正确。
                                            </Tooltip.Content>
                                        </Tooltip>
                                    </div>
                                </div>
                            </Tabs.Panel>
                            <Tabs.Panel id="search_by_qq">
                                <div className="flex flex-col gap-3 py-4">
                                    <TextArea
                                        fullWidth
                                        rows={6}
                                        placeholder="输入收货人绑定的QQ号，一行一个"
                                        value={qqList.join("\n")}
                                        onChange={(e) => setQqList(e.target.value.split("\n"))}
                                        variant="secondary"
                                    />
                                    <div className="flex justify-between items-center text-xs">
                                        <span className={cn(qqInputCount > 100 ? "text-danger" : "text-muted")}>
                                            已输入 {qqInputCount} 个QQ号
                                        </span>
                                        <span className="text-muted">最多100个</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <Button
                                            variant="primary"
                                            onPress={handleBatchQueryQQ}
                                            isPending={qqBatchLoading}
                                            isDisabled={qqInputCount === 0 || qqInputCount > 100}
                                        >
                                            {({ isPending }) => (
                                                <>
                                                    {isPending && <Spinner size="sm" color="current" />}
                                                    {isPending ? "查询中..." : "批量添加"}
                                                </>
                                            )}
                                        </Button>
                                        <Tooltip>
                                            <Tooltip.Trigger>
                                                <div className="flex items-center gap-1 text-muted cursor-help">
                                                    <HelpCircle size={14} />
                                                    <span className="text-xs">帮助</span>
                                                </div>
                                            </Tooltip.Trigger>
                                            <Tooltip.Content>
                                                如果您的目标用户没有绑定QQ号，或者绑定的不是该QQ号，则无法识别。请使用UID搜索或联系收货方确认QQ号后再试。
                                            </Tooltip.Content>
                                        </Tooltip>
                                    </div>
                                </div>
                            </Tabs.Panel>
                        </Tabs>
                    </section>

                    <Separator className="my-8" orientation="horizontal" />

                    {/* STEP 3: 已选收货人 */}
                    <section className="mb-8">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3 flex-wrap">
                                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-accent text-accent-foreground text-xs font-medium">3</span>
                                <h2 className="text-base font-medium text-foreground">已选收货人</h2>
                                {blacklistMatching && (
                                    <div className="flex items-center gap-2 text-xs text-muted">
                                        <Spinner size="sm" color="current" />
                                        系统正在匹配黑名单记录，请稍后....
                                    </div>
                                )}
                                {Object.keys(blacklistHits).length === 0 && !blacklistMatching && selectedUser.length > 0 && (
                                    <div className="flex items-center gap-1 text-xs text-success">
                                        <CheckCircle2 size={14} />
                                        所有用户已通过黑名单检查
                                    </div>
                                )}
                                {Object.keys(blacklistHits).length > 0 && (
                                    <div className="flex items-center gap-1 text-xs text-danger">
                                        <ShieldAlert size={14} />
                                        检测到 {Object.keys(blacklistHits).length} 位收货人命中黑名单
                                    </div>
                                )}
                            </div>
                            {selectedUser.length > 0 && (
                                <span className="text-sm text-muted">{selectedUser.length} 人</span>
                            )}
                        </div>

                        {selectedUser.length === 0 ? (
                            <div className="flex items-center justify-center py-12 bg-surface-secondary rounded-lg border-2 border-dashed border-border">
                                <p className="text-muted text-sm">请先在上方搜索并选择需要发货的用户</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                                {selectedUser.map((user) => (
                                    <div
                                        key={user.uid}
                                        className={cn(
                                            "group relative rounded-lg p-3 transition-colors",
                                            blacklistHits[user.uid]
                                                ? "border border-danger bg-danger/5 hover:bg-danger/10"
                                                : "bg-surface-secondary hover:bg-surface-tertiary",
                                        )}
                                    >
                                        <Button
                                            size="sm"
                                            isIconOnly
                                            aria-label={`移除收货人 ${user.uid}`}
                                            variant="secondary"
                                            className="absolute -top-2 -right-2 rounded-full w-6 h-6 min-w-0 opacity-0 group-hover:opacity-100 transition-opacity"
                                            onPress={() => removeSelectedUser(user)}
                                        >
                                            <X size={14} />
                                        </Button>
                                        <div
                                            className="flex items-center gap-3 cursor-pointer"
                                            onClick={() =>
                                                navigate({
                                                    to: "/template_protections/trusted-query",
                                                    search: { search: user.uid },
                                                })
                                            }
                                        >
                                            <ComponentDynamicUser
                                                uid={user.uid}
                                                blackListed={!!blacklistHits[user.uid]}
                                            />
                                        </div>
                                        <div className="flex items-center gap-3 mt-2 pt-2 border-t border-border">
                                            <span className={cn(
                                                "text-xs",
                                                (userStatistics[user.uid]?.send_count ?? 0) > 0 ? "text-success" : "text-muted"
                                            )}>
                                                发货数 {userStatistics[user.uid]?.send_count ?? '-'}
                                            </span>
                                            <span className={cn(
                                                "text-xs",
                                                (userStatistics[user.uid]?.receive_count ?? 0) > 0 ? "text-accent" : "text-muted"
                                            )}>
                                                收货数 {userStatistics[user.uid]?.receive_count ?? '-'}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>

                    {/* 黑名单命中警告 */}
                    {Object.keys(blacklistHits).length > 0 && (
                        <section className="mb-8">
                            <Alert status="danger">
                                <Alert.Indicator>
                                    <ShieldAlert size={18} />
                                </Alert.Indicator>
                                <Alert.Content>
                                    <Alert.Title>
                                        检测到 {Object.keys(blacklistHits).length} 位收货人命中黑名单
                                    </Alert.Title>
                                    <Alert.Description>
                                        请仔细确认是否继续发货。
                                        <Link
                                            className="text-sm"
                                            onPress={() => {
                                                navigate({ to: "/template_protections/blacklist/self" });
                                            }}
                                        >
                                            管理黑名单
                                        </Link>
                                    </Alert.Description>
                                </Alert.Content>
                            </Alert>
                        </section>
                    )}

                    <Separator className="my-8" orientation="horizontal" />

                    {/* 确认发货区域 */}
                    <section className="max-w-md mx-auto">
                        <Checkbox
                            isDisabled={!selectedUser.length || selectedTemplates.length === 0}
                            isSelected={isConfirmed}
                            onChange={setIsConfirmed}
                        >
                            <Checkbox.Content>
                                <Checkbox.Control>
                                    <Checkbox.Indicator />
                                </Checkbox.Control>
                                <span className="text-sm text-muted">
                                    我已认真核对上述收货人，并确定发货信息无误
                                </span>
                            </Checkbox.Content>
                        </Checkbox>

                        <Button
                            variant="primary"
                            fullWidth
                            className="mt-4 rounded-full"
                            onPress={handleSubmit}
                            isDisabled={!isConfirmed || selectedTemplates.length === 0 || selectedUser.length === 0}
                            isPending={submitLoading}
                            size="lg"
                        >
                            {({ isPending }) => (
                                <>
                                    {isPending ? <Spinner size="sm" color="current" /> : <Package size={18} />}
                                    {isPending ? "发货中..." : `确认发货 ${selectedTemplates.length > 0 ? `(${selectedTemplates.length} 个模板)` : ""}`}
                                </>
                            )}
                        </Button>
                    </section>
                </Surface>
            </div>

            {/* 模板选择 Modal */}
            <Modal.Backdrop
                isOpen={isTemplateModalOpen}
                onOpenChange={setTemplateModalOpen}
            >
                <Modal.Container size="lg" scroll="inside">
                    <Modal.Dialog>
                        {({ close }) => (
                            <>
                                <Modal.Header>
                                    <Modal.Heading>选择模板</Modal.Heading>
                                </Modal.Header>
                                <Modal.Body>
                                    <div className="mb-4">
                                        <div className="relative">
                                            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
                                            <Input
                                                fullWidth
                                                placeholder="输入模板名称搜索..."
                                                value={modalSearchValue}
                                                onChange={(e) => setModalSearchValue(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") {
                                                        handleModalSearch();
                                                    }
                                                }}
                                                className="pl-9 pr-8"
                                            />
                                            {modalSearchValue && (
                                                <Button
                                                    isIconOnly
                                                    size="sm"
                                                    variant="ghost"
                                                    aria-label="清除搜索"
                                                    className="absolute right-2 top-1/2 -translate-y-1/2"
                                                    onPress={() => {
                                                        setModalSearchValue("");
                                                        loadModalTemplates("", 1);
                                                    }}
                                                >
                                                    <X size={18} />
                                                </Button>
                                            )}
                                        </div>
                                    </div>

                                    {modalLoading ? (
                                        <div className="flex items-center justify-center py-12 gap-2">
                                            <Spinner size="sm" color="current" />
                                            <div className="text-muted">加载中...</div>
                                        </div>
                                    ) : modalTemplates.length === 0 ? (
                                        <div className="flex items-center justify-center py-12">
                                            <div className="text-muted">没有找到模板哦～</div>
                                        </div>
                                    ) : (
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            {modalTemplates.map((template) => {
                                                const isSelected = modalSelectedIds.has(template.id);
                                                return (
                                                    <Checkbox
                                                        key={template.id}
                                                        aria-label={`选择模板 ${template.title}`}
                                                        isSelected={isSelected}
                                                        onChange={() => toggleModalTemplateSelection(template.id)}
                                                        className={cn(
                                                            "rounded-lg border transition-all",
                                                            isSelected
                                                                ? "border-accent bg-accent/5"
                                                                : "border-border hover:border-muted",
                                                        )}
                                                    >
                                                        <Checkbox.Content className="flex gap-3 p-3 cursor-pointer">
                                                            <Checkbox.Control>
                                                                <Checkbox.Indicator />
                                                            </Checkbox.Control>
                                                            <div className="w-16 h-16 shrink-0">
                                                                <img
                                                                    alt={template.title}
                                                                    className="w-full h-full object-cover rounded-lg"
                                                                    src={template.preview_url}
                                                                />
                                                            </div>
                                                            <div className="flex flex-col flex-1 min-w-0">
                                                                <span className="text-md font-medium text-foreground truncate">
                                                                    {template.title}
                                                                </span>
                                                                <span className="text-xs text-muted line-clamp-2">
                                                                    {template.description || "暂无模板说明"}
                                                                </span>
                                                                {template.must_realname && (
                                                                    <Chip className="mt-2 rounded-sm text-xs w-fit" size="sm" color="accent" variant="soft">
                                                                        要求实名
                                                                    </Chip>
                                                                )}
                                                            </div>
                                                        </Checkbox.Content>
                                                    </Checkbox>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {modalTotalPages > 1 && (
                                        <Pagination className="mt-4 w-full flex-wrap justify-between gap-2" size="sm">
                                            <Pagination.Summary>
                                                共 {modalTotal} 个模板
                                            </Pagination.Summary>
                                            <Pagination.Content>
                                                <Pagination.Item>
                                                    <Pagination.Previous
                                                        isDisabled={modalPage <= 1 || modalLoading}
                                                        onPress={() => goToModalPage(Math.max(1, modalPage - 1))}
                                                    >
                                                        <Pagination.PreviousIcon />
                                                    </Pagination.Previous>
                                                </Pagination.Item>
                                                {buildPageItems(modalPage, modalTotalPages).map((item, index) =>
                                                    item === "ellipsis" ? (
                                                        <Pagination.Item key={`ellipsis-${index}`}>
                                                            <Pagination.Ellipsis />
                                                        </Pagination.Item>
                                                    ) : (
                                                        <Pagination.Item key={item}>
                                                            <Pagination.Link
                                                                isActive={item === modalPage}
                                                                isDisabled={modalLoading}
                                                                onPress={() => goToModalPage(item)}
                                                            >
                                                                {item}
                                                            </Pagination.Link>
                                                        </Pagination.Item>
                                                    ),
                                                )}
                                                <Pagination.Item>
                                                    <Pagination.Next
                                                        isDisabled={modalPage >= modalTotalPages || modalLoading}
                                                        onPress={() => goToModalPage(Math.min(modalTotalPages, modalPage + 1))}
                                                    >
                                                        <Pagination.NextIcon />
                                                    </Pagination.Next>
                                                </Pagination.Item>
                                            </Pagination.Content>
                                        </Pagination>
                                    )}
                                </Modal.Body>
                                <Modal.Footer>
                                    <Button variant="tertiary" onPress={close}>
                                        取消
                                    </Button>
                                    <Button variant="primary" onPress={handleConfirmTemplates}>
                                        确定 {modalSelectedIds.size > 0 && `(${modalSelectedIds.size})`}
                                    </Button>
                                </Modal.Footer>
                            </>
                        )}
                    </Modal.Dialog>
                </Modal.Container>
            </Modal.Backdrop>
        </section>
    );
}
